import clsx from "clsx";
import { useMutation } from "@tanstack/react-query";
import { LogOut, Users } from "lucide-react";
import type { MouseEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { CaseCounts } from "../../../../shared/contracts.js";
import { useSession } from "../../../app/AppProviders.js";
import { LOGIN_PATH } from "../../../app/RequireSession.js";
import { signOutFailureMessage } from "../../../ui/AsyncPanel.js";
import { Button } from "../../../ui/Button.js";
import { SideSwitch } from "../../wholesale/SideSwitch.js";
import { roleLabel } from "../parts-actions/RecipientWarning.js";
import {
  formatCount,
  PARTS_DESTINATIONS,
  type PartsView,
  type PartsPageDestination,
} from "../presentation.js";
import {
  serializeWorkspaceUrlState,
  type WorkspaceUrlState,
} from "./useWorkspaceUrlState.js";

/**
 * Left navigation of the Parts workspace (spec §10): the Service |
 * Wholesale switch (wholesale spec §4.1 — this is the service side), then
 * New order, Work queue, Arrived · 48h, Needs attention, All orders,
 * History, then the Stock order report (owner request 2026-09-17) — plus
 * who is signed in and Sign out.
 *
 * Every destination is a real link to the list's address, so it opens in a
 * new tab, shows in the status bar and survives a reload. A plain click,
 * though, goes through `onSelectView`: the address in `href` was rendered a
 * moment ago, and a search still being typed commits on the very blur the
 * click causes — the URL-state hook knows about that write, a static `href`
 * does not. Choosing a destination keeps the search and filters and drops
 * the selected case (the inspector belongs to the list it was chosen from).
 * The counts beside Needs attention and Arrived · 48h are the server's — the
 * sidebar never counts rows itself.
 *
 * Below 1280 CSS pixels the workspace hides this column and shows it as a
 * menu the top bar's button opens; `open` and `id` wire that toggle.
 */

export interface PartsSidebarProps {
  /** `id` the collapsed-menu toggle names in `aria-controls`. */
  id: string;
  /** Whether the collapsed menu is open (ignored by the persistent layout). */
  open: boolean;
  state: WorkspaceUrlState;
  counts?: CaseCounts;
  /**
   * Which destination is the current page; defaults to the list in `state`.
   * The order form passes `"new_order"`, the report page
   * `"stock_order_report"` and the catalog `"catalog"` so their own link,
   * not a list, is marked current.
   */
  activeDestination?: PartsView | PartsPageDestination;
  /** Shows a list; a plain click on a destination goes through here. */
  onSelectView: (view: PartsView) => void;
  /** Called after a destination is chosen, so the collapsed menu can close. */
  onNavigate?: () => void;
  className?: string;
}

/** A plain left click the app should handle itself (not new-tab, not a modifier). */
function isPlainClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.defaultPrevented &&
    (!event.currentTarget.target || event.currentTarget.target === "_self")
  );
}

/** The address of `view` with the current search and filters and no selection. */
export function viewPath(state: WorkspaceUrlState, view: PartsView): string {
  const params = serializeWorkspaceUrlState({ ...state, view, caseId: null });
  return `/parts?${params.toString()}`;
}

export function PartsSidebar({
  id,
  open,
  state,
  counts,
  activeDestination = state.view,
  onSelectView,
  onNavigate,
  className,
}: PartsSidebarProps) {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: () => navigate(LOGIN_PATH, { replace: true }),
  });

  return (
    <nav
      id={id}
      className={clsx("workspace__sidebar", className)}
      aria-label="Parts navigation"
      data-open={open ? "true" : "false"}
    >
      <div className="sidebar__brand">
        <span className="sidebar__brand-name">Chicagoland Auto Tools</span>
        <span className="sidebar__brand-role">Parts workspace</span>
      </div>

      <SideSwitch side="service" onNavigate={onNavigate} />

      <ul className="sidebar__destinations">
        {PARTS_DESTINATIONS.map((destination) => {
          const Icon = destination.Icon;
          if (destination.kind === "new_order") {
            return (
              <li key="new_order" className="sidebar__item sidebar__item--action">
                <Link
                  to={destination.to}
                  className="sidebar__link sidebar__link--action"
                  aria-current={activeDestination === "new_order" ? "page" : undefined}
                  onClick={onNavigate}
                >
                  <Icon
                    className="sidebar__icon"
                    aria-hidden="true"
                    focusable="false"
                  />
                  <span className="sidebar__label">{destination.label}</span>
                </Link>
              </li>
            );
          }
          if (destination.kind === "stock_order_report" || destination.kind === "catalog") {
            return (
              <li
                key={destination.kind}
                className={clsx("sidebar__item", destination.kind === "stock_order_report" && "sidebar__item--page")}
              >
                <Link
                  to={destination.to}
                  className="sidebar__link"
                  aria-current={activeDestination === destination.kind ? "page" : undefined}
                  onClick={onNavigate}
                >
                  <Icon
                    className="sidebar__icon"
                    aria-hidden="true"
                    focusable="false"
                  />
                  <span className="sidebar__label">{destination.label}</span>
                </Link>
              </li>
            );
          }
          const active = destination.view === activeDestination;
          const count =
            destination.countKey && counts
              ? counts[destination.countKey]
              : null;
          return (
            <li key={destination.view} className="sidebar__item">
              <Link
                to={viewPath(state, destination.view)}
                className="sidebar__link"
                aria-current={active ? "page" : undefined}
                onClick={(event) => {
                  if (isPlainClick(event)) {
                    event.preventDefault();
                    onSelectView(destination.view);
                  }
                  onNavigate?.();
                }}
              >
                <Icon
                  className="sidebar__icon"
                  aria-hidden="true"
                  focusable="false"
                />
                <span className="sidebar__label">{destination.label}</span>
              </Link>
              {/* Outside the link so the destination's name stays exactly its label. */}
              {count !== null && count > 0 ? (
                <span className="sidebar__count">
                  <span className="visually-hidden">Count: </span>
                  {formatCount(count)}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>

      <div className="sidebar__session">
        {user ? (
          <p className="sidebar__user">
            <span className="visually-hidden">Signed in as </span>
            <span className="sidebar__user-name">{user.displayName}</span>
            <span className="sidebar__user-role">{roleLabel(user.role)}</span>
          </p>
        ) : null}
        {user?.isAdmin ? (
          // Administration is a capability on the account, not a role (spec §2).
          <Link to="/accounts" className="sidebar__admin-link">
            <Users aria-hidden="true" />
            Accounts
          </Link>
        ) : null}
        {signOut.isError ? (
          <p role="alert" className="form-alert sidebar__alert">
            {signOutFailureMessage(signOut.error)}
          </p>
        ) : null}
        <Button
          variant="quiet"
          icon={<LogOut />}
          busy={signOut.isPending}
          onClick={() => signOut.mutate()}
          className="sidebar__sign-out"
        >
          Sign out
        </Button>
      </div>
    </nav>
  );
}
